import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { presenceService } from '../services/presenceService';
import eventService from '../services/eventService';

const PresenceContext = createContext();

export const PresenceProvider = ({ children }) => {
  const { user } = useAuth();
  const [userPresence, setUserPresence] = useState({}); // { userID: { status: 'online', lastSeen: 'timestamp' } }

  // Subscribe to real-time presence updates via Event API
  useEffect(() => {
    if (!user?.id) return;



    const handlePresenceUpdate = (presenceUpdate) => {
      setUserPresence(prev => ({
        ...prev,
        [presenceUpdate.userID]: {
          status: presenceUpdate.status,
          lastSeen: presenceUpdate.lastSeen,
        },
      }));
    };

    const subscription = eventService.subscribeToPresence(handlePresenceUpdate);

    return () => {
      eventService.unsubscribe('presence_updates');
    };
  }, [user?.id]);

  // Function to get a user's status
  const getUserStatus = useCallback((userID) => {
    if (userID === user?.id) return 'online'; // Current user is always online from their perspective
    const presence = userPresence[userID];
    if (!presence) return 'offline'; // Default to offline if no data

    // Basic logic: consider online if status is 'online' and lastSeen is recent (e.g., within 5 minutes)
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const lastSeenDate = new Date(presence.lastSeen);

    if (presence.status === 'online' && lastSeenDate > fiveMinutesAgo) {
      return 'online';
    } else if (presence.status === 'offline') {
      return 'offline';
    } else {
      // Could be 'away' or just not updated recently
      return 'offline'; // For simplicity, treat as offline if not explicitly recent online
    }
  }, [user?.id, userPresence]);

  // Update own presence with real-time Event API
  useEffect(() => {
    if (!user?.id) return;

    // Set online status on mount
    eventService.updatePresence(user.id, 'online');

    const handleVisibilityChange = () => {
      if (document.hidden) {
        eventService.updatePresence(user.id, 'away');
      } else {
        eventService.updatePresence(user.id, 'online');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Heartbeat every 2 minutes (reduced from 4 for better accuracy)
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        eventService.updatePresence(user.id, 'online');
      }
    }, 2 * 60 * 1000);

    // Handle page unload
    const handleBeforeUnload = () => {
      eventService.updatePresence(user.id, 'offline');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Set offline on unmount
      eventService.updatePresence(user.id, 'offline');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(intervalId);
    };
  }, [user?.id]);

  // Manual presence update function
  const updateUserPresence = useCallback(async (userID, status) => {
    try {
      await eventService.updatePresence(userID, status);
    } catch (error) {
      console.error('Error updating presence:', error);
    }
  }, []);

  const value = {
    userPresence,
    getUserStatus,
    updateUserPresence,
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};